import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { roleApi } from '@/services/roleApi';
import { RoleFormData } from '@/types';
import { ArrowLeft, Shield, Check, ShieldCheck, Lock, Eye, PlusCircle, Pencil, Trash2, CheckCircle, Truck } from 'lucide-react';

// Professional labels for resources
const RESOURCE_LABELS: Record<string, { label: string; icon: string; description: string }> = {
  dashboard: { label: 'Dashboard', icon: '📊', description: 'Access to dashboard and analytics' },
  service: { label: 'Services', icon: '🔧', description: 'Service management and operations' },
  customer: { label: 'Customers', icon: '👥', description: 'Customer data and profiles' },
  invoice: { label: 'Invoices', icon: '📄', description: 'Invoice creation and management' },
  payment: { label: 'Payments', icon: '💳', description: 'Payment processing and tracking' },
  employee: { label: 'Employees', icon: '👤', description: 'Employee management' },
  branch: { label: 'Branches', icon: '🏢', description: 'Branch management' },
  report: { label: 'Reports', icon: '📈', description: 'Reports and analytics' },
  part: { label: 'Parts', icon: '🔩', description: 'Spare parts and components management' },
  task: { label: 'Tasks', icon: '✅', description: 'Task assignment and tracking' },
  expense: { label: 'Expenses', icon: '💰', description: 'Expense management' },
  petty_cash: { label: 'Petty Cash', icon: '💵', description: 'Petty cash management' },
  cash_settlement: { label: 'Cash Settlement', icon: '🏦', description: 'Daily cash settlement and verification' },
  master: { label: 'Master Data', icon: '⚙️', description: 'Master data configuration' },
  inventory: { label: 'Inventory', icon: '📦', description: 'Stock inventory and warehouse management' },
  customer_order: { label: 'Customer Orders', icon: '🛒', description: 'Customer order management' },
  settings: { label: 'Settings', icon: '⚙️', description: 'System settings' },
  service_category: { label: 'Service Categories', icon: '🧩', description: 'Service categories & custom service-creation forms' },
  purchase: { label: 'Purchases', icon: '🧾', description: 'Suppliers, purchase orders, bills and returns' },
  financial_report: { label: 'Financial Reports', icon: '📊', description: 'Profit & Loss, Balance Sheet, Cash Flow, Tally export' },
  discount: { label: 'Discounts', icon: '🏷️', description: 'Discount rules and management' },
  sla: { label: 'SLA Policies', icon: '⏱️', description: 'Service-level agreement policies' },
  template: { label: 'Templates & Themes', icon: '🖼️', description: 'Invoice/job-sheet templates, designer and themes' },
  referral: { label: 'Referrals', icon: '🤝', description: 'Referral program management' },
  role: { label: 'Roles', icon: '🔐', description: 'Role and permission management' },
};

// Professional labels for actions
const ACTION_LABELS: Record<string, { label: string; color: string; bgColor: string }> = {
  view: { label: 'View', color: 'text-blue-700', bgColor: 'bg-blue-50 border-blue-200' },
  create: { label: 'Create', color: 'text-green-700', bgColor: 'bg-green-50 border-green-200' },
  update: { label: 'Edit', color: 'text-amber-700', bgColor: 'bg-amber-50 border-amber-200' },
  delete: { label: 'Delete', color: 'text-red-700', bgColor: 'bg-red-50 border-red-200' },
  assign: { label: 'Assign', color: 'text-indigo-700', bgColor: 'bg-indigo-50 border-indigo-200' },
  deliver: { label: 'Deliver to Customer', color: 'text-teal-700', bgColor: 'bg-teal-50 border-teal-200' },
  export: { label: 'Export', color: 'text-cyan-700', bgColor: 'bg-cyan-50 border-cyan-200' },
  status_update: { label: 'Verify Settlement', color: 'text-purple-700', bgColor: 'bg-purple-50 border-purple-200' },
};

// Get action icon
const getActionIcon = (action: string) => {
  switch (action) {
    case 'view': return <Eye className="w-3.5 h-3.5" />;
    case 'create': return <PlusCircle className="w-3.5 h-3.5" />;
    case 'update': return <Pencil className="w-3.5 h-3.5" />;
    case 'delete': return <Trash2 className="w-3.5 h-3.5" />;
    case 'deliver': return <Truck className="w-3.5 h-3.5" />;
    case 'status_update': return <ShieldCheck className="w-3.5 h-3.5" />;
    default: return <Check className="w-3.5 h-3.5" />;
  }
};

export default function CreateRole() {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [formData, setFormData] = useState<RoleFormData>({
    name: '',
    description: '',
    permissionIds: [],
  });
  const [selectedPermissions, setSelectedPermissions] = useState<Set<string>>(new Set());

  // Fetch permissions grouped by resource
  const { data: permissionsGrouped } = useQuery({
    queryKey: ['permissions-grouped'],
    queryFn: roleApi.getPermissionsGrouped,
  });

  const createMutation = useMutation({
    mutationFn: roleApi.createRole,
    onSuccess: () => {
      navigate('/roles');
    },
    onError: (error: any) => {
      const responseData = error.response?.data;

      // Check if there are validation errors
      if (responseData?.errors && Array.isArray(responseData.errors)) {
        const errorMessages = responseData.errors.map((err: any) => err.message);
        setValidationErrors(errorMessages);
        setError('Please fix the following validation errors:');
      } else {
        setError(responseData?.message || 'Failed to create role');
        setValidationErrors([]);
      }
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setValidationErrors([]);

    const data: RoleFormData = {
      name: formData.name,
      description: formData.description || undefined,
      permissionIds: Array.from(selectedPermissions),
    };

    await createMutation.mutateAsync(data);
  };

  const togglePermission = (permissionId: string) => {
    const newSelected = new Set(selectedPermissions);
    if (newSelected.has(permissionId)) {
      newSelected.delete(permissionId);
    } else {
      newSelected.add(permissionId);
    }
    setSelectedPermissions(newSelected);
  };

  const selectAllInResource = (resourcePerms: any[]) => {
    const newSelected = new Set(selectedPermissions);
    const allSelected = resourcePerms.every(p => newSelected.has(p.id));

    resourcePerms.forEach(p => {
      if (allSelected) {
        newSelected.delete(p.id);
      } else {
        newSelected.add(p.id);
      }
    });

    setSelectedPermissions(newSelected);
  };

  return (
    <>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/roles')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Create New Role</h1>
            <p className="text-gray-600 mt-1">Add a new custom role with specific permissions</p>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-600 font-semibold">{error}</p>
            {validationErrors.length > 0 && (
              <ul className="mt-2 ml-4 list-disc text-red-600 text-sm space-y-1">
                {validationErrors.map((err, index) => (
                  <li key={index}>{err}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-white rounded-xl p-6 shadow-sm space-y-6">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Shield className="h-5 w-5 text-purple-600" />
              Role Information
            </h2>

            <div className="grid grid-cols-1 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Role Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="e.g., Sales Manager"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Describe this role's responsibilities..."
                />
              </div>
            </div>
          </div>

          {/* Permissions */}
          <div className="bg-white rounded-xl p-6 shadow-sm space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Lock className="h-5 w-5 text-purple-600" />
                Permissions
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Selected {selectedPermissions.size} permission(s) - Enable or disable access for this role
              </p>
            </div>

            <div className="max-h-[600px] overflow-y-auto space-y-4 pr-1">
              {permissionsGrouped?.map((group) => {
                const resourceConfig = RESOURCE_LABELS[group.resource] || {
                  label: group.resource.replace('_', ' '),
                  icon: '📋',
                  description: ''
                };
                const allSelected = group.permissions.every(p => selectedPermissions.has(p.id));
                const someSelected = group.permissions.some(p => selectedPermissions.has(p.id));

                // Separate regular actions and special actions (like status_update)
                const regularActions = group.permissions.filter(p => p.action !== 'status_update');
                const specialActions = group.permissions.filter(p => p.action === 'status_update');

                return (
                  <div
                    key={group.resource}
                    className={`border rounded-lg overflow-hidden transition-all ${
                      someSelected ? 'border-purple-300 bg-purple-50/30' : 'border-gray-200'
                    }`}
                  >
                    {/* Resource Header */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 border-b border-gray-200">
                      <div className="flex items-center gap-3">
                        <span className="text-xl">{resourceConfig.icon}</span>
                        <div>
                          <h3 className="font-semibold text-gray-900 capitalize">
                            {resourceConfig.label}
                          </h3>
                          {resourceConfig.description && (
                            <p className="text-xs text-gray-500">{resourceConfig.description}</p>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => selectAllInResource(group.permissions)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                          allSelected
                            ? 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {allSelected ? 'Disable All' : 'Enable All'}
                      </button>
                    </div>

                    {/* Regular Actions Grid */}
                    <div className="p-4">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {regularActions.map((permission) => {
                          const actionConfig = ACTION_LABELS[permission.action] || {
                            label: permission.action,
                            color: 'text-gray-700',
                            bgColor: 'bg-gray-50 border-gray-200'
                          };
                          const isSelected = selectedPermissions.has(permission.id);

                          return (
                            <button
                              key={permission.id}
                              type="button"
                              onClick={() => togglePermission(permission.id)}
                              className={`flex items-center gap-2 p-2.5 rounded-lg border transition-all ${
                                isSelected
                                  ? `${actionConfig.bgColor} ${actionConfig.color} border-current`
                                  : 'bg-white border-gray-200 text-gray-400 hover:border-gray-300'
                              }`}
                            >
                              <div className={`flex items-center justify-center w-5 h-5 rounded ${
                                isSelected ? 'bg-current/10' : 'bg-gray-100'
                              }`}>
                                {getActionIcon(permission.action)}
                              </div>
                              <span className="text-sm font-medium">{actionConfig.label}</span>
                              {isSelected && (
                                <CheckCircle className="w-4 h-4 ml-auto" />
                              )}
                            </button>
                          );
                        })}
                      </div>

                      {/* Special Actions (like Approve/Reject for Cash Settlement) */}
                      {specialActions.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          {specialActions.map((permission) => {
                            const isSelected = selectedPermissions.has(permission.id);

                            return (
                              <div
                                key={permission.id}
                                className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                                  isSelected
                                    ? 'bg-purple-50 border-purple-300'
                                    : 'bg-gray-50 border-gray-200'
                                }`}
                              >
                                <div className="flex items-center gap-3">
                                  <div className={`flex items-center justify-center w-10 h-10 rounded-lg ${
                                    isSelected ? 'bg-purple-100' : 'bg-gray-200'
                                  }`}>
                                    <ShieldCheck className={`w-5 h-5 ${isSelected ? 'text-purple-600' : 'text-gray-400'}`} />
                                  </div>
                                  <div>
                                    <p className={`font-semibold ${isSelected ? 'text-purple-700' : 'text-gray-600'}`}>
                                      Approve / Reject Settlements
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      Verify or reject submitted cash settlements (requires Security PIN)
                                    </p>
                                  </div>
                                </div>

                                {/* Toggle Switch */}
                                <button
                                  type="button"
                                  onClick={() => togglePermission(permission.id)}
                                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                                    isSelected ? 'bg-purple-600' : 'bg-gray-200'
                                  }`}
                                  role="switch"
                                  aria-checked={isSelected}
                                >
                                  <span
                                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                      isSelected ? 'translate-x-5' : 'translate-x-0'
                                    }`}
                                  />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-4">
            <button
              type="button"
              onClick={() => navigate('/roles')}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="px-6 py-2 bg-gradient-to-r from-purple-600 to-purple-500 text-white rounded-lg hover:from-purple-700 hover:to-purple-600 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {createMutation.isPending ? 'Creating...' : 'Create Role'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
